/**
 * Metadata form pieces shared by the asset page and the "New placeholder" dialog:
 * one input per field type, and the sections of the model grouped into tabs
 * (when the model defines tabs) with an error count per tab.
 */
import { Fragment, useState } from 'react';
import { X } from 'lucide-react';
import { fieldTabId, isFieldVisible, metadataTabs, missingNeededFields, sectionTabId, type FieldDefinition, type MetadataSchemaDefinition } from '@prism/contracts';
import { useServerPref } from '../lib/prefs';

/** Marks a Needed field that is still empty: the form still saves, but the asset's metadata counts as incomplete. */
function NeededMark() {
  return <span className="needed-mark" title="Needed: the asset's metadata is incomplete until this has a value">Needed</span>;
}

/** A stored date-time (UTC, 1.0.0-rc.10) as a datetime-local input's value, in this browser's time zone. */
export function toLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** A datetime-local input's value (this browser's time zone) as the stored UTC form; null when empty. */
export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function Field({ field, value, error, onChange, readOnly = false, missing = false }: { field: FieldDefinition; value: unknown; error?: string; onChange: (v: unknown) => void; readOnly?: boolean; missing?: boolean }) {
  const id = `field-${field.id}`;
  const cls = error ? 'invalid' : '';
  let control;
  if (readOnly) {
    const text = value == null || value === '' ? '—'
      : field.type === 'boolean' ? (value ? 'Yes' : 'No')
      : field.type === 'datetime' && typeof value === 'string' ? toLocalInput(value).replace('T', ' ') || value
      : Array.isArray(value) ? value.map((v) => field.options?.find((o) => o.value === v)?.label ?? v).join(', ')
      : field.options?.find((o) => o.value === value)?.label ?? String(value);
    return (
      <div className={`field ${missing ? 'needed-empty' : ''}`} data-field={field.id}>
        <label htmlFor={id}>{field.label}{missing && <NeededMark />}</label>
        <div id={id} className="readonly-value">{text}</div>
      </div>
    );
  }
  if (field.type === 'vocabulary' && field.multiple) {
    const list = (value as string[] | undefined) ?? [];
    return (
      <div className={`field ${missing ? 'needed-empty' : ''}`} data-field={field.id}>
        <label>{field.label}{field.required && <span className="req" aria-label="required">*</span>}{missing && <NeededMark />}</label>
        <div className="tags" role="group" aria-label={field.label}>
          {field.options?.map((o) => (
            <button type="button" key={o.value} className={`tag toggle ${list.includes(o.value) ? 'on' : ''}`} aria-pressed={list.includes(o.value)}
              onClick={() => onChange(list.includes(o.value) ? list.filter((v) => v !== o.value) : [...list, o.value])}>{o.label}</button>
          ))}
        </div>
        {error ? <span className="error-text" role="alert">{error}</span> : field.help && <span className="help">{field.help}</span>}
      </div>
    );
  }
  switch (field.type) {
    case 'rich_text':
      control = <textarea id={id} className={`textarea ${cls}`} value={(value as string) ?? ''} maxLength={field.maxLength} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'vocabulary':
      control = (
        <select id={id} className={`select ${cls}`} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
      break;
    case 'tags':
      control = <TagInput id={id} value={(value as string[]) ?? []} onChange={onChange} />;
      break;
    case 'date':
      control = <input id={id} type="date" className={`input ${cls}`} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)} />;
      break;
    case 'datetime':
      // Entered in this browser's time zone, stored in UTC (1.0.0-rc.10).
      control = <input id={id} type="datetime-local" className={`input ${cls}`} value={toLocalInput(value)} onChange={(e) => onChange(fromLocalInput(e.target.value))} />;
      break;
    case 'number':
      control = <input id={id} type="number" className={`input ${cls}`} value={(value as number) ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
      break;
    case 'boolean':
      control = <input id={id} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />;
      break;
    default:
      control = <input id={id} className={`input ${cls}`} value={(value as string) ?? ''} maxLength={field.maxLength} onChange={(e) => onChange(e.target.value)} placeholder={field.type === 'timecode' ? 'HH:MM:SS:FF' : undefined} />;
  }
  return (
    <div className={`field ${missing ? 'needed-empty' : ''}`} data-field={field.id}>
      <label htmlFor={id}>{field.label}{field.required && <span className="req" aria-label="required">*</span>}{missing && <NeededMark />}</label>
      {control}
      {error ? <span className="error-text" role="alert">{error}</span> : field.help && <span className="help">{field.help}</span>}
    </div>
  );
}

function TagInput({ id, value, onChange }: { id: string; value: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim().replace(/,$/, '');
    if (t && !value.includes(t)) onChange([...value, t]);
    setDraft('');
  };
  return (
    <div className="tag-input" onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.focus()}>
      {value.map((t) => (
        <span className="tag" key={t}>{t}<button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))}><X size={11} /></button></span>
      ))}
      <input id={id} value={draft} placeholder={value.length ? '' : 'Add keywords…'} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add} />
    </div>
  );
}

/**
 * The model's sections as form fieldsets. With more than one tab, a sub-tab bar picks which tab's sections
 * show; a tab holding fields with errors shows a red count. The chosen tab is remembered for the person.
 * Values live in the caller, so edits survive switching tabs.
 */
export function MetadataSections({ schema, values, categoryId, errors, onChange, canEdit, editable, renderAfterField }: {
  schema: MetadataSchemaDefinition;
  values: Record<string, unknown>;
  /** The asset's class (category id): fields limited to other classes are not shown. */
  categoryId: string | null;
  errors: Record<string, string>;
  onChange: (id: string, v: unknown) => void;
  canEdit: boolean;
  /** Fields the person may edit (false = shown read-only); absent means all. */
  editable?: Record<string, boolean>;
  /** 1.4.0: shown under each field (the asset page's inherited value, G2's InheritedFieldValue). */
  renderAfterField?: (field: FieldDefinition, value: unknown) => React.ReactNode;
}) {
  const tabs = metadataTabs(schema);
  const [remembered, setRemembered] = useServerPref<string>('asset.metadata-tab', '');
  const [chosen, setChosen] = useState<string | null>(null);
  const current = tabs.find((t) => t.id === (chosen ?? remembered))?.id ?? tabs[0]?.id ?? null;
  const visibleFields = (fields: FieldDefinition[]) => fields.filter((f) => isFieldVisible(f, values, categoryId));
  const sections = schema.sections.map((s) => ({ ...s, fields: schema.fields.filter((f) => (f.section ?? schema.sections[0]?.id) === s.id) }));
  const shown = tabs.length > 1 ? sections.filter((s) => sectionTabId(schema, s) === current) : sections;
  const errorCount = (tabId: string) => schema.fields.filter((f) => errors[f.id] && fieldTabId(schema, f) === tabId && isFieldVisible(f, values, categoryId)).length;
  // Needed fields without a value are highlighted (required ones show their error when saving instead).
  const missing = new Set(missingNeededFields(schema, values, categoryId).filter((f) => !f.required).map((f) => f.id));

  return (
    <>
      {tabs.length > 1 && (
        <div className="subtabs" role="tablist" aria-label="Metadata tabs" data-testid="metadata-subtabs">
          {tabs.map((t) => {
            const n = errorCount(t.id);
            const needs = schema.fields.filter((f) => missing.has(f.id) && fieldTabId(schema, f) === t.id && isFieldVisible(f, values, categoryId)).length;
            return (
              <button key={t.id} type="button" role="tab" className="subtab" aria-selected={current === t.id} data-testid={`metadata-tab-${t.id}`}
                onClick={() => { setChosen(t.id); setRemembered(t.id); }}>
                {t.label}
                {needs > 0 && <span className="subtab-needed" role="status" aria-label={`${needs} needed field${needs === 1 ? '' : 's'} empty`} title={`${needs} needed field${needs === 1 ? '' : 's'} empty`} />}
                {n > 0 && <span className="subtab-errors" role="status" aria-label={`${n} field${n === 1 ? '' : 's'} need attention`}>{n}</span>}
              </button>
            );
          })}
        </div>
      )}
      {shown.map((s) => (
        <fieldset key={s.id} className="form-section" style={{ border: 0, padding: 0, margin: 0 }} disabled={!canEdit}>
          <legend className="section-label" style={{ padding: 0 }}>{s.label}</legend>
          {visibleFields(s.fields).map((f) => (
            <Fragment key={f.id}>
              <Field field={f} value={values[f.id]} error={errors[f.id]} onChange={(v) => onChange(f.id, v)} readOnly={editable ? !editable[f.id] : false} missing={missing.has(f.id)} />
              {renderAfterField?.(f, values[f.id])}
            </Fragment>
          ))}
        </fieldset>
      ))}
    </>
  );
}
